import React from 'react';
import { Stack } from '@mui/material';

import { DivisionLine } from '@paragon/contract-library';
import { NumericField } from '@paragon/material';

import { findAndSetReferenceLine } from '../../../../store/actions/editorActions';
import { startSetReferenceLineGuid } from '../../../../store/actions/insertActions';
import { getAsyncRequestForActiveComponentDesign } from '../../../../store/selectors/getAsyncRequestForActiveComponentDesignSelector';
import { getReferenceLineLongNamesByGuidForActiveComponent } from '../../../../store/selectors/getReferenceLineLongNamesByGuidForActiveComponentSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import ReferenceLineNameProperty from '../elements/ReferenceLineNameProperty';
import ReferenceLineProperty from '../elements/ReferenceLineProperty';
import { ReferenceLineTypeProperty } from '../elements/ReferenceLineTypeProperty';

interface Props {
  line: DivisionLine;
  afterUpdateLine?: () => void;
}

const DivisionLineProperties = ({ line, afterUpdateLine }: Props) => {
  const dispatch = useAppDispatch();
  const referenceLineLongNamesByGuid = useAppSelector(getReferenceLineLongNamesByGuidForActiveComponent);
  const disabled = useAppSelector((state) => {
    const asyncRequest = getAsyncRequestForActiveComponentDesign(state);
    return propertiesShouldBeViewOnly(state) || (!!asyncRequest && asyncRequest.type !== 'updateFeatures');
  });

  const onApplyDivisionFactor = async (newDivisionFactor: number) => {
    const newLine = { ...line, divisionFactor: newDivisionFactor };
    await dispatch(findAndSetReferenceLine(newLine));
    afterUpdateLine && afterUpdateLine();
  };

  if (!referenceLineLongNamesByGuid) {
    throw new Error('Rendering DivisionLineSubPanel, but active component reference line names are missing.');
  }

  return (
    <Stack direction="column" spacing={2}>
      <ReferenceLineNameProperty sx={{ marginBottom: '0!important' }} line={line} disabled={disabled} />
      <ReferenceLineTypeProperty line={line} disabled={disabled} />

      <ReferenceLineProperty
        referenceLineLongNamesByGuid={referenceLineLongNamesByGuid}
        line={line}
        disabled={disabled}
        lineKey="line1Guid"
        propertyTitle="Point line 1"
        startSetReferenceLineGuid={(referenceLine, key) => dispatch(startSetReferenceLineGuid(referenceLine, key))}
      />

      <ReferenceLineProperty
        referenceLineLongNamesByGuid={referenceLineLongNamesByGuid}
        line={line}
        disabled={disabled}
        lineKey="line2Guid"
        propertyTitle="Point line 2"
        startSetReferenceLineGuid={(referenceLine, key) => dispatch(startSetReferenceLineGuid(referenceLine, key))}
      />

      <NumericField
        value={line.divisionFactor}
        label="Division factor"
        onSubmit={onApplyDivisionFactor}
        disabled={disabled}
      />
    </Stack>
  );
};

export default DivisionLineProperties;
